import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

const TAMANHOS = {
  xs: "w-3 h-3",
  sm: "w-3.5 h-3.5",
  md: "w-4 h-4",
  lg: "w-5 h-5",
  xl: "w-7 h-7",
} as const;

interface StarsProps {
  /** Nota de 0 a 5. Aceita fração — 4,7 pinta a quinta estrela em 70%. */
  value: number;
  size?: keyof typeof TAMANHOS;
  className?: string;
}

/**
 * Cinco estrelas com preenchimento fracionário.
 *
 * A fração é feita com uma estrela cheia recortada por `width` sobre uma
 * estrela vazia, e não com meia-estrela arredondada: a média é o número que
 * mais é olhado no bloco, e arredondar 4,6 para "quatro e meia" desalinha o
 * desenho do número escrito ao lado dele.
 *
 * `aria-hidden`: quem chama sempre expõe a nota como texto ("4,9 de 5"), então
 * o leitor de tela leria a informação duas vezes.
 */
export function Stars({ value, size = "md", className }: StarsProps) {
  const nota = Math.max(0, Math.min(5, value));
  const cls = TAMANHOS[size];

  return (
    <span
      className={cn("inline-flex items-center gap-0.5 shrink-0", className)}
      aria-hidden="true"
    >
      {[0, 1, 2, 3, 4].map((i) => {
        const preenchimento = Math.max(0, Math.min(1, nota - i));
        return (
          <span key={i} className={cn("relative inline-block", cls)}>
            <Star
              className={cn(cls, "absolute inset-0 text-lp-rating/30")}
              strokeWidth={1.5}
            />
            {preenchimento > 0 && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${preenchimento * 100}%` }}
              >
                <Star
                  className={cn(cls, "text-lp-rating fill-lp-rating")}
                  strokeWidth={1.5}
                />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Estrelas clicáveis do formulário. Separado de `Stars` porque as duas coisas
 * divergem em tudo que importa: aqui há foco, teclado, hover e estado — lá é
 * um desenho estático.
 */
interface StarsInputProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const ROTULOS = [
  "",
  "Ruim",
  "Fraco",
  "Regular",
  "Bom",
  "Excelente",
] as const;

export function StarsInput({ value, onChange, disabled }: StarsInputProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Nota de 1 a 5 estrelas"
      className="flex items-center gap-1"
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} ${n === 1 ? "estrela" : "estrelas"} — ${ROTULOS[n]}`}
          disabled={disabled}
          onClick={() => onChange(n)}
          className={cn(
            "group rounded-md p-1 transition-transform",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            !disabled && "hover:scale-110 active:scale-95",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          <Star
            className={cn(
              "w-9 h-9 sm:w-10 sm:h-10 transition-colors",
              n <= value
                ? "text-lp-rating fill-lp-rating"
                : "text-muted-foreground/35 group-hover:text-lp-rating/60",
            )}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

export { ROTULOS as ROTULOS_NOTA };
